import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import type { components } from '../api/schema'

// Recoverable charges (spec 2026-10-25-09): the rule catalogue of the
// business rules page and the proposal shown on every charge line.

export type RecoverableProposal = components['schemas']['RecoverableProposal']
export type RecoverableDecision = components['schemas']['RecoverableDecision']
export type ChargeAllocationRule = components['schemas']['ChargeAllocationRule']
export type ChargeAllocationRules = components['schemas']['ChargeAllocationRules']
export type ChargeAllocationOverrideInput = components['schemas']['ChargeAllocationOverrideInput']
export type RecoverableValue = ChargeAllocationRule['recoverable']

function invalidate(qc: QueryClient) {
  qc.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey as unknown[]
      return key[0] === 'get' && typeof key[1] === 'string' &&
        (key[1].includes('/expenses') || key[1].includes('/charge-allocation-rules') || key[1].includes('/validation'))
    },
  })
  qc.invalidateQueries({ queryKey: ['intervention-validation'] })
}

export function useChargeAllocationRules(org: string, jurisdiction: string | undefined, enabled = true) {
  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/charge-allocation-rules', {
    params: { path: { orgCode: org }, query: jurisdiction ? { jurisdiction } : undefined },
  }, { enabled: enabled && !!org })
}

export function useCreateChargeOverride(org: string) {
  const qc = useQueryClient()
  const m = $api.useMutation('post', '/api/v1/organizations/{orgCode}/charge-allocation-rules', {
    onSuccess: () => invalidate(qc),
  })
  return {
    ...m,
    mutateAsync: (body: ChargeAllocationOverrideInput) => m.mutateAsync({ params: { path: { orgCode: org } }, body }),
  }
}

export function useUpdateChargeOverride(org: string) {
  const qc = useQueryClient()
  const m = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/charge-allocation-rules/{ruleUid}', {
    onSuccess: () => invalidate(qc),
  })
  return {
    ...m,
    mutateAsync: (ruleUid: string, body: { recoverable?: RecoverableValue; recoverable_ratio?: number | null; note?: string }) =>
      m.mutateAsync({ params: { path: { orgCode: org, ruleUid } }, body }),
  }
}

export function useDeleteChargeOverride(org: string) {
  const qc = useQueryClient()
  const m = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/charge-allocation-rules/{ruleUid}', {
    onSuccess: () => invalidate(qc),
  })
  return {
    ...m,
    mutateAsync: (ruleUid: string) => m.mutateAsync({ params: { path: { orgCode: org, ruleUid } } }),
  }
}

export function useAcceptRecoverableProposals(org: string) {
  const qc = useQueryClient()
  const m = $api.useMutation('post', '/api/v1/organizations/{orgCode}/expenses/recoverable-proposals/accept', {
    onSuccess: () => invalidate(qc),
  })
  return {
    ...m,
    mutateAsync: (expenseUids: string[], expected?: Record<string, string>) =>
      m.mutateAsync({ params: { path: { orgCode: org } }, body: { expense_uids: expenseUids, expected_rule_uids: expected } }),
  }
}

/** The value of a decision or a proposal as one word: yes, partial or no. */
export function recoverableKey(recoverable: boolean | null | undefined, ratio?: number | null): 'yes' | 'partial' | 'no' | 'unqualified' {
  if (recoverable == null) return 'unqualified'
  if (!recoverable) return 'no'
  return ratio != null ? 'partial' : 'yes'
}

/** A ratio in (0, 1) as a whole percentage. */
export function percent(ratio: number | null | undefined): number {
  return Math.round((ratio ?? 0) * 100)
}
